import { StyleSheet, Text, View } from 'react-native';

import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants/primitives';

interface GreetingSectionProps {
  userName: string;
  currentPoints: number;
  displayedPoints: number;
  reachedLastTier: boolean;
}

export const GreetingSection = ({
  userName,
  currentPoints,
  displayedPoints,
  reachedLastTier,
}: GreetingSectionProps) => {
  const getGreetingMessage = () => {
    if (currentPoints === 0) {
      return t('letsGetStarted', { userName });
    }

    if (reachedLastTier) {
      return t('topLevelUnlocked', { userName });
    }

    return t('niceWork', { userName });
  };

  const getPointsLabel = () => {
    return currentPoints > 0 ? t('availableCrowns') : t('crowns');
  };

  const getAccessibilityLabel = () => {
    const greeting = getGreetingMessage();
    const pointsLabel = getPointsLabel();
    return `${greeting} ${displayedPoints} ${pointsLabel}`;
  };

  return (
    <View
      style={styles.greetingSection}
      accessible={true}
      accessibilityRole="text"
      accessibilityLabel={getAccessibilityLabel()}
      testID="greeting-section"
    >
      <Text style={styles.greetingText} accessibilityRole="header" accessibilityLevel={2}>
        {getGreetingMessage()}
      </Text>
      <Text
        style={styles.points}
        accessibilityRole="text"
        accessibilityLabel={`${displayedPoints} points`}
        accessibilityLiveRegion="polite"
      >
        {displayedPoints}
      </Text>
      <Text
        style={styles.pointsLabel}
        accessibilityRole="text"
        accessibilityLabel={getPointsLabel()}
      >
        {getPointsLabel()}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  greetingSection: { zIndex: 2, alignItems: 'center', marginBottom: 20 },
  greetingText: {
    lineHeight: 36,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: primitive.fontSizes.md,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
  points: {
    lineHeight: 36,
    fontSize: primitive.fontSizes['4xl'],
    fontFamily: primitive.fontFamily.$bold,
    color: primitive.bk.$bbqBrown,
  },
  pointsLabel: {
    lineHeight: 21,
    fontSize: primitive.fontSizes.sm,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
  },
});
